import { useToast } from "@chakra-ui/react";
import * as Sentry from "@sentry/react";
import { SpanStatusType } from "@sentry/tracing";
import {
  buildReportFileName,
  ReportPeriodType,
  buildReportShortName,
} from "@syla/shared/helpers/buildReportFileName";
import React, { useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { errorToToastOptions } from "../../../api/errors/errorToToastOptions";
import { downloadTaxReportForFinancialYear } from "../../../api/pdf/downloadTaxReportForFinancialYear";
import reportPdf from "../../../images/icons/reportPdf.svg";
import { useCurrentAccountStore } from "../../../store/currentAccountStore";
import { FinancialYearOption } from "../../../types/user/financialYearOption";
import { errorToastOptions } from "../../molecules/errorToastOptions";
import { DocumentRow } from "./DocumentRow";

interface TaxReportRowProps {
  selectedFinancialYear: FinancialYearOption | undefined;
  isLoading?: boolean;
  canDownload: boolean;
  canPurchase: boolean;
  sampleUrl?: string;
}

export const TaxReportRow = ({
  selectedFinancialYear,
  isLoading,
  canDownload,
  sampleUrl,
  canPurchase,
}: TaxReportRowProps): JSX.Element => {
  const toast = useToast();

  const { accountId, fullName, contactEmail } = useCurrentAccountStore(
    useShallow((state) => ({
      accountId: state.accountId,
      fullName: state.fullName,
      contactEmail: state.contactEmail,
    })),
  );

  const shortName =
    selectedFinancialYear ?
      buildReportShortName({
        name: "Crypto Tax Report",
        period: {
          type: ReportPeriodType.FinancialYear,
          start: selectedFinancialYear?.startDate,
          end: selectedFinancialYear?.endDate,
          timezone: "Australia/Brisbane",
        },
      })
    : "";

  const [downloading, setDownloading] = useState(false);

  // handle download click
  const handleDownload = async () => {
    if (!selectedFinancialYear) {
      toast({
        ...errorToastOptions,
        title: "Financial year not selected",
      });

      return;
    }

    const reportName = buildReportFileName({
      name: "Crypto Tax Report",
      period: {
        type: ReportPeriodType.FinancialYear,
        start: selectedFinancialYear?.startDate,
        end: selectedFinancialYear?.endDate,
        timezone: "Australia/Brisbane",
      },
      user: {
        contactEmail: contactEmail,
        fullName: fullName,
      },
    });

    // instrument
    const transaction = Sentry.startTransaction({
      name: "/tax-reports/downloadTaxReportForFinancialYear",
      data: { financialYearValue: selectedFinancialYear.value },
    });

    const span = transaction.startChild({
      op: "task",
      description: `downloadTaxReportForFinancialYear`,
    });

    try {
      setDownloading(true);
      // Set transaction on scope to associate with errors and get included span instrumentation
      Sentry.getCurrentHub().configureScope((scope) =>
        scope.setSpan(transaction),
      );
      await downloadTaxReportForFinancialYear(
        accountId,
        selectedFinancialYear.value,
        reportName,
      );
      span.setStatus("ok" as SpanStatusType);
    } catch (error) {
      span.setStatus("unknown_error" as SpanStatusType);

      toast({
        ...errorToToastOptions(error),
        title: "Tax report could not be downloaded.",
      });
    }

    span.finish();
    transaction.finish();

    setDownloading(false);
  };

  return (
    <DocumentRow
      isLoading={isLoading}
      downloading={downloading}
      canDownload={canDownload}
      image={reportPdf}
      sampleUrl={sampleUrl}
      documentName={shortName}
      handleDownload={handleDownload}
      canPurchase={canPurchase}
    />
  );
};
